import { useCallback, useEffect, useState } from 'react';
import type { AdminListResponse } from '../../../domain/superadmin/types';
import type { AdminPartnerRow } from '../../../domain/partners/adminTypes';
import { fetchAdminPartners, updateAdminPartner, promotePartnerCommissions } from '../../../services/modules/partners/partnerAdminApi';
import { SuperadminApiError } from '../../../services/modules/superadmin/errors';
import { useAppContext } from '../../../contexts/AppContext';
import { notify } from '../../../utils/toast';

const PAGE_SIZE = 25;

export function useAdminPartners() {
  const { setCurrentPage } = useAppContext();
  const [data, setData] = useState<AdminListResponse<AdminPartnerRow> | null>(null);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchAdminPartners({
        page,
        pageSize: PAGE_SIZE,
        q: q || undefined,
        status: status || undefined,
      });
      setData(result);
    } catch (e) {
      if (e instanceof SuperadminApiError && e.isForbidden) {
        setCurrentPage('dashboard');
        notify.warning('Acesso não autorizado à área de superadmin.');
        return;
      }
      setError(e instanceof Error ? e.message : 'Erro ao carregar parceiros');
    } finally {
      setIsLoading(false);
    }
  }, [page, q, status, setCurrentPage]);

  useEffect(() => {
    void load();
  }, [load]);

  const updatePartner = useCallback(async (partnerId: string, patch: { status?: string; commissionRate?: number }) => {
    setIsSaving(true);
    try {
      await updateAdminPartner(partnerId, patch);
      notify.success('Parceiro atualizado');
      await load();
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Erro ao atualizar parceiro');
    } finally {
      setIsSaving(false);
    }
  }, [load]);

  const runPromoteCommissions = useCallback(async () => {
    setIsSaving(true);
    try {
      const result = await promotePartnerCommissions();
      notify.success(`${result.promoted} comissões promovidas`);
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Erro ao promover comissões');
    } finally {
      setIsSaving(false);
    }
  }, []);

  return {
    data,
    isLoading,
    isSaving,
    error,
    refetch: load,
    updatePartner,
    runPromoteCommissions,
    page,
    setPage,
    q,
    setQ,
    status,
    setStatus,
    pageSize: PAGE_SIZE,
  };
}
